/**
 * Version Manager - Automatic cache busting system
 * 
 * This module handles version checking and cache clearing when a new
 * version of the application is deployed.
 */

const VERSION_KEY = 'app_version';
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

interface VersionInfo {
    version: string;
    buildTime: string;
    hash: string;
}

/**
 * Fetches the current version from the server
 */
async function fetchServerVersion(): Promise<VersionInfo | null> {
    try {
        // Add cache-busting query parameter to prevent caching of version.json
        const response = await fetch(`/version.json?t=${Date.now()}`, {
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
            }
        });

        if (!response.ok) {
            console.warn('[VersionManager] Failed to fetch version.json:', response.status);
            return null;
        }

        return await response.json();
    } catch (error) {
        console.warn('[VersionManager] Error fetching version:', error);
        return null;
    }
}

/**
 * Gets the stored version from localStorage
 */
function getStoredVersion(): string | null {
    try {
        return localStorage.getItem(VERSION_KEY);
    } catch {
        return null;
    }
}

/**
 * Stores the current version in localStorage
 */
function storeVersion(version: string): void {
    try {
        localStorage.setItem(VERSION_KEY, version);
    } catch (error) {
        console.warn('[VersionManager] Failed to store version:', error);
    }
}

/**
 * Clears all application caches
 */
async function clearAllCaches(): Promise<void> {
    console.log('[VersionManager] Clearing all caches...');

    // Clear Service Worker caches
    if ('caches' in window) {
        try {
            const cacheNames = await caches.keys();
            await Promise.all(
                cacheNames.map(cacheName => {
                    console.log('[VersionManager] Deleting cache:', cacheName);
                    return caches.delete(cacheName);
                })
            );
        } catch (error) {
            console.warn('[VersionManager] Error clearing caches:', error);
        }
    }

    // Unregister all service workers
    if ('serviceWorker' in navigator) {
        try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(
                registrations.map(registration => {
                    console.log('[VersionManager] Unregistering service worker');
                    return registration.unregister();
                })
            );
        } catch (error) {
            console.warn('[VersionManager] Error unregistering service workers:', error);
        }
    }

    // Clear sessionStorage (but keep certain keys if needed)
    try {
        sessionStorage.clear();
    } catch (error) {
        console.warn('[VersionManager] Error clearing sessionStorage:', error);
    }

    console.log('[VersionManager] Cache clearing complete');
}

/**
 * Performs a hard reload of the application
 */
function hardReload(): void {
    console.log('[VersionManager] Performing hard reload...');
    // Use location.reload(true) for a hard reload (deprecated but still works)
    // or navigate to the same URL with a cache-busting parameter
    window.location.href = window.location.pathname + '?v=' + Date.now();
}

/**
 * Checks if a new version is available and handles cache clearing
 * @returns true if a new version was detected and handled
 */
export async function checkVersion(): Promise<boolean> {
    const serverVersion = await fetchServerVersion();

    if (!serverVersion) {
        // In development or if version.json doesn't exist, skip version check
        if (import.meta.env.DEV) {
            console.log('[VersionManager] Development mode - skipping version check');
        }
        return false;
    }

    const storedVersion = getStoredVersion();
    const currentVersion = serverVersion.hash || serverVersion.version;

    console.log('[VersionManager] Server version:', currentVersion);
    console.log('[VersionManager] Stored version:', storedVersion);

    if (!storedVersion) {
        // First time visit - just store the version
        console.log('[VersionManager] First visit - storing version');
        storeVersion(currentVersion);
        return false;
    }

    if (storedVersion !== currentVersion) {
        console.log('[VersionManager] New version detected! Clearing cache...');

        // Clear caches
        await clearAllCaches();

        // Store new version
        storeVersion(currentVersion);

        // Notify user and reload
        showUpdateNotification();

        return true;
    }

    console.log('[VersionManager] Version is up to date');
    return false;
}

/**
 * Shows a notification to the user about the update
 */
function showUpdateNotification(): void {
    // Create a simple notification banner
    const banner = document.createElement('div');
    banner.id = 'version-update-banner';
    banner.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #166534;
      color: white;
      padding: 12px 16px;
      text-align: center;
      z-index: 99999;
      font-family: system-ui, -apple-system, sans-serif;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    ">
      <span style="margin-right: 16px; font-weight: 500;">
        🔄 Une nouvelle version est disponible !
      </span>
      <button 
        onclick="window.location.reload()" 
        style="
          background: white;
          color: #166534;
          border: none;
          padding: 6px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          transition: transform 0.2s;
        "
        onmouseover="this.style.transform='scale(1.05)'"
        onmouseout="this.style.transform='scale(1)'"
      >
        Actualiser maintenant
      </button>
    </div>
  `;

    document.body.appendChild(banner);
}

/**
 * Starts periodic version checking
 */
export function startVersionMonitoring(): void {
    // Skip in development
    if (import.meta.env.DEV) {
        console.log('[VersionManager] Development mode - version monitoring disabled');
        return;
    }

    // Initial check after a short delay to not block app startup
    setTimeout(() => {
        checkVersion();
    }, 3000);

    // Periodic checks
    setInterval(() => {
        checkVersion();
    }, VERSION_CHECK_INTERVAL);

    // Check when tab becomes visible (user returns to the app)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            checkVersion();
        }
    });

    // Check on focus (additional trigger)
    window.addEventListener('focus', () => {
        checkVersion();
    });

    console.log('[VersionManager] Version monitoring started');
}

/**
 * Forces a cache clear and reload (can be called manually)
 */
export async function forceRefresh(): Promise<void> {
    await clearAllCaches();
    localStorage.removeItem(VERSION_KEY);
    hardReload();
}

/**
 * Gets the current app version info
 */
export async function getVersionInfo(): Promise<VersionInfo | null> {
    return fetchServerVersion();
}
